/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  ExtendedHttpErrorResponse,
  IAPIErrorObject,
} from '../models/error.models';

export function deepCopy<T>(value: T): T {
  if (typeof structuredClone === 'function') {
    try {
      return structuredClone(value);
    } catch {
      // just in case this still throws an error fall back to the old method
      return JSON.parse(JSON.stringify(value));
    }
  } else {
    return JSON.parse(JSON.stringify(value));
  }
}

/**
 * Makes object that doesn't have any `null` or `undefined` or empty arrays.
 *
 * @param obj
 * @param deleteEmptyArray if set to false, will not remove empty arrays --> `[]`
 */
export function makeObjectWithNoNullValues(
  obj: any,
  deleteEmptyArray = true,
): Record<string, any> {
  const params: Record<string, any> = {};

  Object.keys(obj).forEach(key => {
    if (
      obj[key] == null ||
      obj[key] === undefined ||
      (deleteEmptyArray && Array.isArray(obj[key]) && !obj[key].length)
    ) {
      return;
    } else {
      params[key] = obj[key];
    }
  });

  return params;
}

/**
 * Check if two arrays have equal values. They don't need to have the same order of values.
 *
 * example:
 * ```
 * areArrayValuesEqual([1,2,3,4], [1,2,3,4]) // returns true
 * areArrayValuesEqual([1,2,3,4], [4,3,2,1]) // returns true
 * areArrayValuesEqual([1,2,3,4], [1,2,3])   // returns false
 * areArrayValuesEqual([1,2,3,4], [1,2,3,5]) // returns false
 * ```
 *
 * @param arr1
 * @param arr2
 * @returns true if arrays have the same values
 */
export function areArrayValuesEqual(arr1: any[], arr2: any[]): boolean {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every(v => arr2.includes(v));
}

export function copyToClipboard(text: string): boolean {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  const isCopied = document.execCommand('copy');
  document.body.removeChild(el);

  return isCopied;
}

/**
 * Create an anchor element and initiate a file download.
 *
 * @param data
 * @param title needs to end with an extension
 * @param mimeType example: 'text/csv'
 */
export function downloadFile(
  data: any,
  title: string,
  mimeType: string = 'text',
): void {
  // Create anchor element
  const a = window.document.createElement('a');
  // Set data object as download value
  a.href = window.URL.createObjectURL(new Blob([data], { type: mimeType }));
  // Set title
  a.download = title;
  // Append anchor to body.
  document.body.appendChild(a);
  a.click();
  // Remove anchor from body
  document.body.removeChild(a);
}

/**
 * Gets the percentage value for a number.
 *
 * Formula `((n - min) / (max - min)) * 100`
 *
 * @param value
 * @param min
 * @param max
 */
export function getPercentage(value: number, min = 0, max = 100): number {
  return Math.abs(((value - min) / (max - min)) * 100);
}

/**
 * Capitalizes every first letter of a word in the given string
 *
 * @param value
 */
export function capitalize(value: string): string {
  const str = value.split(' ');

  for (let i = 0, x = str.length; i < x; i++) {
    str[i] = str[i][0].toUpperCase() + str[i].substr(1);
  }

  return str.join(' ');
}

/**
 * Convert base64 string to a Blob type
 *
 * @param str
 */
export function base64ImageToBlob(str: string): Blob {
  // extract content type and base64 payload from original string
  const pos = str.indexOf(';base64,');
  const type = str.substring(5, pos);
  const b64 = str.substr(pos + 8);

  // decode base64
  const imageContent = atob(b64);

  // create an ArrayBuffer and a view (as unsigned 8-bit)
  const buffer = new ArrayBuffer(imageContent.length);
  const view = new Uint8Array(buffer);

  // fill the view, using the decoded base64
  for (let n = 0; n < imageContent.length; n++) {
    view[n] = imageContent.charCodeAt(n);
  }

  // convert ArrayBuffer to Blob
  const blob = new Blob([buffer], { type });

  return blob;
}

/**
 * Removes all spaces from a given word and makes all first letters capital case
 *
 * example. "How it works" --> "HowItWorks"
 * @param word
 */
export function makeTitleCase(word: string): string {
  return word
    .split(' ')
    .map(c => c.slice(0, 1).toUpperCase() + c.slice(1))
    .join('');
}

/**
 * Flattens multi dimension array into 1D array
 *
 * ex. [ [ ], [ ], [ [ ], [ ] ] ] --> [...]
 * @param arr
 */
export function flattenArrayDeep<T>(arr: any): T[] {
  return arr.reduce((acc: any, e: any) => {
    return Array.isArray(e) ? acc.concat(flattenArrayDeep(e)) : acc.concat(e);
  }, []);
}

/**
 * Get a message that might exist in the `error.debug.exception` array that the API returns.
 *
 * @param err
 * @param transloco
 */
export function getDebugExceptionMessageFromHTTPErrorResponse(
  err: ExtendedHttpErrorResponse<IAPIErrorObject>,
): string {
  const error_debug = err?.error?.debug;

  if (!error_debug?.exception?.length) return '';

  return error_debug.exception[0].message || '';
}

export class MapWithDefault<K, V> extends Map<K, V> {
  constructor(defaultValue: V, entries?: readonly (readonly [K, V])[]);
  constructor(
    private defaultValue: V,
    entries?: Iterable<readonly [K, V]>,
  ) {
    super(entries);
  }

  /**
   * Get a value from the map. If the values doesn't exist it will return the default value.
   *
   * @param key
   */
  override get(key: K): V {
    if (this.has(key)) {
      return super.get(key) as V;
    }

    return this.defaultValue;
  }
}

/**
 * Return the wanted value if it exists in the map, otherwise returns the last value in the map's value which
 * is always is_sandoz.
 * Map values must be an array.
 *
 * @param map
 * @param key
 * @param wantedValue
 */
export function getMapValueIfExists<KEY, VALUE>(
  map: MapWithDefault<KEY, VALUE[]>,
  key: KEY,
  wantedValue: VALUE,
): VALUE {
  return map.get(key).includes(wantedValue)
    ? wantedValue
    : map.get(key).at(-1)!;
}
